/* The chain-status block's numbers (Ale rulings 2026-09-03).
 *
 * "37 vs 34 vs 32 is confusing": the block must never show numbers that
 * do not visibly add up. So the matrix is bucketed ONCE, every demand in
 * exactly one bucket — the fund itself (its own entity's demands), one
 * bucket per chain level (the objects on the legs at that depth and the
 * trades anchored to them), and "elsewhere on the record" for whatever
 * the chain does not reach — and the buckets SUM to the headline. WAIVED
 * COUNTS AS PROVEN: a demand waived with its reason is settled.
 *
 * Pure (imports only the equally pure `chainTree`) so the bare node
 * runner can prove the reconciliation — see tests/unit/chainStatus.test.mjs. */

import { chainDepths, fundRootId, levelNames, type ChainSegment } from './chainTree.js'

export interface StatusRow {
  object_id: string
  status: 'open' | 'requested' | 'pending_signoff' | 'satisfied' | 'waived'
  /** Absent on older payloads — then no demand is counted as optional. */
  is_mandatory?: boolean
}

export interface StatusTrade {
  id: string
  position_id: string | null
  instrument_id: string | null
}

export interface Fraction {
  proven: number
  total: number
}

export interface LevelStatus extends Fraction {
  depth: number
  holders: string[]
  issuers: string[]
}

export interface ChainStatus {
  /** ``owed`` = every unsettled demand (pending included — the headline
   *  never hides a demand); ``optional`` = how many of the owed ones
   *  policy only recommends, so the sentence can say "5 are still owed,
   *  3 of them optional" (Ale 2026-09-04) without moving the count. */
  overall: Fraction & { pending: number; owed: number; optional: number }
  /** The fund's own entity's demands. */
  fund: Fraction
  /** One per chain level (depth ≥ 1), in depth order. */
  levels: LevelStatus[]
  /** Demands on objects the chain does not reach. */
  elsewhere: Fraction
}

const FUND = 0
const ELSEWHERE = -1

export function chainStatus<S extends ChainSegment>(
  chain: S[],
  trades: StatusTrade[],
  matrix: StatusRow[]
): ChainStatus {
  const legDepth = chainDepths(chain)
  const root = fundRootId(chain)
  // object → bucket: a leg's objects (its positions, its instrument, its
  // issuer) at the leg's depth; a trade where what it moved sits; the
  // fund's own entity in its own bucket.
  const bucketOf = new Map<string, number>()
  if (root) bucketOf.set(root, FUND)
  for (const s of chain) {
    const d = legDepth.get(s.position_id) ?? s.depth
    for (const pid of s.position_ids.length ? s.position_ids : [s.position_id]) bucketOf.set(pid, d)
    if (s.instrument_id) bucketOf.set(s.instrument_id, d)
    if (s.issuer_id && s.issuer_id !== root) bucketOf.set(s.issuer_id, d)
  }
  for (const t of trades) {
    const anchor = t.position_id ?? t.instrument_id
    if (anchor && bucketOf.has(anchor)) bucketOf.set(t.id, bucketOf.get(anchor) as number)
  }

  const cells = new Map<number, Fraction>()
  const cell = (b: number) => {
    let c = cells.get(b)
    if (!c) {
      c = { proven: 0, total: 0 }
      cells.set(b, c)
    }
    return c
  }
  let proven = 0
  let pending = 0
  let owed = 0
  let optional = 0
  for (const r of matrix) {
    const settled = r.status === 'satisfied' || r.status === 'waived'
    if (settled) proven += 1
    else if (r.status === 'pending_signoff') pending += 1
    else owed += 1
    if (!settled && r.is_mandatory === false) optional += 1
    const c = cell(bucketOf.get(r.object_id) ?? ELSEWHERE)
    c.total += 1
    if (settled) c.proven += 1
  }
  const names = new Map(levelNames(chain).map((l) => [l.depth, l]))
  const levels = [...cells.keys()]
    .filter((b) => b > FUND)
    .sort((a, b) => a - b)
    .map((depth) => ({
      depth,
      holders: names.get(depth)?.holders ?? [],
      issuers: names.get(depth)?.issuers ?? [],
      ...(cells.get(depth) as Fraction),
    }))
  return {
    overall: { proven, total: matrix.length, pending, owed, optional },
    fund: cells.get(FUND) ?? { proven: 0, total: 0 },
    levels,
    elsewhere: cells.get(ELSEWHERE) ?? { proven: 0, total: 0 },
  }
}

/** THE RULE: the parts sum to the headline, proven and total alike. */
export function reconciles(s: ChainStatus): boolean {
  const parts = [s.fund, ...s.levels, s.elsewhere]
  const proven = parts.reduce((n, p) => n + p.proven, 0)
  const total = parts.reduce((n, p) => n + p.total, 0)
  return proven === s.overall.proven && total === s.overall.total
}

/** A one-level fund shows the headline alone (Ale: nothing to reconcile
 *  against, so no second number). Two or more levels show every part. */
export function showParts(s: ChainStatus): boolean {
  return s.levels.length >= 2
}
